import { useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { UploadCloud } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import type { GameVersion } from '@/models'

/* Matches VersionsController::UPLOAD_STATUSES — every status but archived. */
const UPLOADABLE_STATUSES = new Set(['draft', 'in_review', 'rejected', 'released'])

interface AssetBundleUploadCardProps {
  version: GameVersion
  canEdit: boolean
}

export function AssetBundleUploadCard({ version, canEdit }: AssetBundleUploadCardProps) {
  const { toast } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (!canEdit || !UPLOADABLE_STATUSES.has(version.status)) return null

  const clear = () => {
    setFile(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const submit = () => {
    if (!file) return
    router.post(
      `/versions/${version.id}/asset_bundles`,
      { files: [file] },
      {
        forceFormData: true,
        preserveScroll: true,
        onStart: () => setSubmitting(true),
        onFinish: () => setSubmitting(false),
        onSuccess: () => {
          clear()
          toast('Asset bundle uploaded', 'success')
        },
        onError: (errors) => toast(errors.asset_bundles ?? 'Upload failed', 'danger'),
      },
    )
  }

  return (
    <Card className="space-y-3 p-5">
      <div>
        <h3 className="text-fg text-sm font-semibold">Upload asset bundle</h3>
        <p className="text-fg-subtle mt-0.5 text-xs">
          Pick the Addressables export ZIP — must contain{' '}
          <span className="font-mono">settings.json</span>, <span className="font-mono">catalog.bin</span>,{' '}
          <span className="font-mono">catalog.hash</span>, and a <span className="font-mono">bundles/</span>{' '}
          folder. Replaces the whole package.
        </p>
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="border-border bg-bg-subtle text-fg-subtle hover:border-border-strong hover:text-fg-muted focus-visible:outline-accent flex h-24 w-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed px-4 text-center transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-1"
      >
        <UploadCloud className="size-4" />
        <span className="text-sm">{file ? file.name : 'Choose a .zip file'}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip"
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        className="hidden"
      />

      <div className="flex justify-end gap-2">
        {file && (
          <Button size="sm" variant="outline" onClick={clear} disabled={submitting}>
            Clear
          </Button>
        )}
        <Button size="sm" onClick={submit} disabled={!file || submitting}>
          Upload
        </Button>
      </div>
    </Card>
  )
}
